/**
 * C2 — "What each organisation can see". The caller's projects down the side,
 * the organisations across the top, every cell a grant they made and can change.
 *
 * Story #5648 build v2 turned this from a self-fetching component into a
 * prop-driven one. The same grants now answer three questions on one screen —
 * this grid, each portfolio card's "You share: 3 projects · 2 folders hidden",
 * and the strip's "4 of your 6 projects" — and three components fetching them
 * separately is three chances for the same screen to contradict itself. The page
 * reads them once; sharingModel does the arithmetic; everyone renders the same
 * answer.
 *
 * The columns are every organisation that appears anywhere on the screen, not
 * just the ones already granted: a portfolio the caller is part of but has
 * granted nothing gets a column full of "— not granted  Grant…", which is
 * exactly how they give it its first one. A ★ marks the organisations whose
 * portfolio they are part of, matching the legend above.
 *
 * A project with a live IP arrangement carries a small pill beside its name
 * ("IP: option proposed"). It is a LABEL, not a control: an arrangement never
 * changes a single cell in this grid, which is the whole reason the pill sits
 * next to the project name rather than inside anybody's column.
 */
import OrganisationVisibilityActions, { GrantCellAction } from './OrganisationVisibilityActions'
import { dayAndMonth } from './sharingModel'
import type { ProjectGrants } from './sharingModel'
import type { AggregatorGrant } from '../../types'
import { ipPillLabel, ipPillTitle } from './ipModel'
import type { IpArrangement } from '../../types/ipArrangements'

export interface OrganisationColumn {
  id: string
  name: string
  inPortfolio: boolean
}

export interface OrganisationVisibilityTableProps {
  projects: ProjectGrants[]
  columns: OrganisationColumn[]
  /** A real read failure on at least one row — never confused with "nothing granted". */
  anyErrored: boolean
  /**
   * The live IP arrangement on each project, keyed by project id. Absent while
   * the read is in flight, which is why a missing entry renders no pill rather
   * than a "no arrangement" claim.
   */
  ipByProject?: Record<string, IpArrangement>
}

function cellLabel(grant: AggregatorGrant): string {
  if (grant.expires_at) {
    const on = dayAndMonth(new Date(grant.expires_at * 1000).toISOString())
    return `Granted (expires ${on})`
  }
  if (grant.exclusions.length > 0) {
    return `Granted · ${grant.exclusions.length} folder${grant.exclusions.length === 1 ? '' : 's'} hidden`
  }
  return 'Granted'
}

function cellTone(grant: AggregatorGrant): string {
  if (grant.exclusions.length > 0) return 'font-semibold text-amber-700'
  return 'font-semibold text-emerald-700'
}

export default function OrganisationVisibilityTable({
  projects, columns, anyErrored, ipByProject = {},
}: OrganisationVisibilityTableProps) {
  /**
   * DENSE ONLY WHEN IT HAS TO BE. The figure's grid is 12.5px in the cells
   * (`table.vis{font-size:12.5px}`), 11.5px in the headers (`.vis th`) and 11px
   * in the Actions buttons (`.vis .act .btn`) — three sizes, deliberately. The
   * round-6 fix for the wrapping Actions pair dropped the CELLS to 11px too, at
   * every column count, so bob's single roomy column was shrunk to solve a
   * problem it does not have; the round-7 review measured "Granted" at 48x8
   * where the figure is 55x9.5.
   *
   * Measured, not judged (Chrome 131, the built stylesheet, the real font
   * stack, script in /tmp/measure): the widest cell content is "— not granted
   * Grant…" at 143.9px at 12.5px and 127.4px at 11px, plus 16px of px-2
   * padding. charlie's grid card is 1118px wide (the navy header band in the
   * round-7 capture, x=289..1406 at a 1440 viewport), so with Project at 180px
   * and Actions at 210px each organisation column gets (1118 - 390) / n:
   *
   *   n=4 -> 182.0px available, 159.9px needed at 12.5px   fits
   *   n=5 -> 145.6px available, 159.9px needed at 12.5px   does NOT fit
   *                             143.4px needed at 11px     fits, 2.2px spare
   *
   * So five is the measured breakpoint and four is not: shrink at five.
   */
  const dense = columns.length >= 5
  const cellText = dense ? 'text-[11px]' : 'text-[12.5px]'
  const headText = dense ? 'text-[11px]' : 'text-[11.5px]'

  return (
    // The heading, the subhead and every state below them live INSIDE this
    // part's root — including the empty one. An inventor who has granted nothing
    // must still see "What each organisation can see" and be told, in words,
    // that nothing is granted; a section that disappears when it is empty is
    // indistinguishable from a section that failed to load, and on this screen
    // that ambiguity is the whole problem.
    <section data-part="OrganisationVisibilityTable" aria-label="What each organisation can see" className="mb-5">
      <h2 className="mb-2 flex flex-wrap items-baseline gap-2 text-[14px] font-bold text-slate-900">
        What each organisation can see
        <span className="text-[11.5px] font-medium text-slate-500">
          · your projects × the organisations you&apos;ve granted — every cell is
          a grant you made and can change
        </span>
      </h2>

      {/* A fetch error must never look like "nothing shared" — the worst
          possible failure mode on a screen whose whole promise is showing
          exactly who can see what. */}
      {anyErrored && (
        <p className="mb-2 text-sm text-rose-600">
          Could not load some of your sharing settings — the table below may be
          incomplete.
        </p>
      )}

      {/* The wrapper still scrolls, but only as a genuine last resort: the
          table below is table-fixed and fits its card at five organisations, so
          in the reviewed shapes nothing overflows at all. Where an inventor has
          granted enough organisations that it does, the scrollbar is made
          VISIBLE (round 5 clipped the Actions column with no affordance
          whatsoever, which is the worst of both) and the Actions column is
          sticky to the right edge, so the two controls stay reachable however
          far the matrix runs. */}
      <div className="overflow-x-auto [scrollbar-color:theme(colors.slate.400)_transparent] [scrollbar-width:thin]">
        {columns.length === 0 && !anyErrored ? (
          <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            No organisation can see any of your projects. Nothing granted.
          </p>
        ) : (
          /* `table-fixed w-full` — the figure's own `table.vis{width:100%}`,
             plus the layout algorithm that makes the declared column widths
             BINDING rather than advisory.
             
             This is the third attempt at the same 210px cell and the first that
             is stable, so it is worth writing down why. Round 4 was `w-full`
             with `w-[210px]`: auto layout treats a width as a suggestion, so
             five organisation columns squeezed Actions until "Hide folder…"
             broke over two lines and every row stood ~100px tall. Round 5 made
             the widths minimums and the table `min-w-full`: the widths held,
             but the table then grew past its card and was clipped at the card's
             right edge — no Actions header at all and one glyph of "H" per row.
             Neither is the figure, and the second was worse.
             
             table-fixed settles it: Project and Actions take their declared
             widths and the organisation columns divide whatever is left,
             narrowing and wrapping their headers as they must. The table is
             exactly its card's width at any column count, so it can neither
             squeeze Actions nor escape the card. */
          <table className="w-full table-fixed border-collapse overflow-hidden rounded-xl border border-slate-200 bg-white text-[12.5px]">
            <thead>
              <tr className="bg-iflow-900 text-left text-white">
                {/* The figure's Project column is 250px in a two-organisation
                    grid. It gives up width first, because a project name
                    truncates gracefully and a control does not. */}
                <th className="w-[180px] px-3 py-2 text-[11.5px] font-semibold">Project</th>
                {/* No width: the organisation columns divide what is left, and
                    their headers WRAP onto two lines rather than forcing the
                    table wider. That is the trade the figure's own header
                    styling already allows (padding 8px 12px, 11.5px, no
                    nowrap) — this only makes it explicit at five columns. */}
                {columns.map((col) => (
                  <th key={col.id} className={`whitespace-normal break-words px-2 py-2 font-semibold leading-tight ${headText}`}>
                    {col.name}
                    {col.inPortfolio && (
                      <span title="You are part of this portfolio — an inventor never shows you more than they granted"> ★</span>
                    )}
                  </th>
                ))}
                {/* The figure's Actions cell is 210px with its pair on one
                    no-wrap line (`.vis .act{white-space:nowrap}`). Under
                    table-fixed this width is binding, and `sticky right-0`
                    keeps the column against the card's right edge if the table
                    ever does scroll — the round-5 failure was this header not
                    being drawn at all. */}
                <th className="sticky right-0 z-10 w-[210px] whitespace-nowrap bg-iflow-900 px-3 py-2 text-right text-[11.5px] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((row) => {
                // A row whose read has not settled knows NOTHING about this
                // project yet, and on a privacy screen "Nothing granted" is a
                // claim, not a placeholder — it is the single most reassuring
                // thing this table can say, so saying it before the answer is in
                // is worse than saying nothing (Bug #5839).
                if (row.status === 'loading') {
                  return (
                    <tr key={row.project.project_id} className="border-t border-slate-200">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.project.name}</td>
                      <td colSpan={columns.length + 1} className="px-3 py-2 text-slate-400">Checking…</td>
                    </tr>
                  )
                }
                if (row.status === 'not-owner') {
                  return (
                    <tr key={row.project.project_id} className="border-t border-slate-200">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.project.name}</td>
                      <td colSpan={columns.length + 1} className="px-3 py-2 text-slate-500">
                        Not yours to grant — shared with you
                      </td>
                    </tr>
                  )
                }
                if (row.status === 'error') {
                  return (
                    <tr key={row.project.project_id} className="border-t border-slate-200">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.project.name}</td>
                      <td colSpan={columns.length + 1} className="px-3 py-2 text-rose-600">could not load</td>
                    </tr>
                  )
                }
                const byOrg = new Map(row.grants.map((g) => [g.aggregator_id, g]))
                const ip = ipByProject[row.project.project_id]
                const pill = ipPillLabel(ip)
                return (
                  <tr key={row.project.project_id} className="border-t border-slate-200">
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {row.project.name}
                      {pill && ip && (
                        <span
                          data-part="IpPill"
                          title={ipPillTitle(ip)}
                          className="ml-1.5 inline-block whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-800"
                        >
                          {pill}
                        </span>
                      )}
                    </td>
                    {columns.map((col) => {
                      const grant = byOrg.get(col.id)
                      // px-2 and 11px, not px-3 and 12.5px: the narrower the
                      // organisation columns get, the more of their room has to
                      // go to the words rather than to padding, and "— not
                      // granted  Grant…" has to stay on one line at five
                      // columns.
                      return (
                        <td key={col.id} className={`px-2 py-2 text-slate-600 ${cellText}`}>
                          {grant ? (
                            <span className={cellTone(grant)}>{cellLabel(grant)}</span>
                          ) : (
                            // ONE LINE, as the figure draws it: "— not granted
                            // Grant…". Round 3 let the button wrap under the
                            // words, which turned a one-line matrix cell into a
                            // two-line one on every ungranted cell — and with
                            // five organisations across, that is the whole grid
                            // twice as tall.
                            <span className="inline-flex items-baseline whitespace-nowrap">
                              — not granted
                              <GrantCellAction
                                projectId={row.project.project_id}
                                aggregatorId={col.id}
                                aggregatorName={col.name}
                              />
                            </span>
                          )}
                        </td>
                      )
                    })}
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-2 text-right align-middle">
                      <OrganisationVisibilityActions
                        projectId={row.project.project_id}
                        grants={row.grants}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
